// Story 11.10 (ADO-1961): the Info Panel an `InfoButton` opens (the designer's 3 Nov export,
// frames 00, M1 to M5, and the M5 CSS export). Content in the component's slots: a title, a
// definition, then optional steps (one line each), a result and a note.
//
// Two presentations, one content:
// - `default` (desktop, 296px) and `touch` (tablet, 288px, a 44px close): a NON-MODAL panel 8px
//   below the button, aligned to its left edge (its right edge near the screen edge), placed as the
//   `Popover` is (`useAnchoredPlacement`). Focus stays on the button (frame 00), and goes back to it
//   when the close is pressed. Esc, the close or a press or tap outside closes it
//   (`useDismissAnchoredPanel`); the button toggles it. It is announced
//   when it opens because it is inserted into a polite live region that is already in the page
//   (a region mounted together with its content is often not read).
// - `sheet` (phones): `Modal`'s bottom sheet, the modal dialog the mobile multi-buy dialog is drawn
//   as: the scrim, the Sheet Header (grab handle, title, 44px close), a focus trap, and Esc, the
//   close, a scrim press or a swipe down to close. Focus goes back to the button when it closes.

import { useEffect, useId, useRef } from 'react';
import type { CSSProperties, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../Icon/Icon';
import { Modal } from '../Modal/Modal';
import { useAnchoredPlacement, useDismissAnchoredPanel } from '../hooks/useAnchoredPlacement';
import styles from './InfoPanel.module.css';

export type InfoPanelSize = 'default' | 'touch' | 'sheet';

export interface InfoPanelContent {
  /** Heads the panel and names it for assistive tech. */
  readonly title: string;
  readonly definition: string;
  /** One line each; left out when there are none. */
  readonly steps?: readonly string[] | undefined;
  readonly result?: string | undefined;
  readonly note?: string | undefined;
}

export interface InfoPanelProps extends InfoPanelContent {
  readonly open: boolean;
  readonly onClose: () => void;
  /** The `InfoButton` that opens it: the panel's anchor, and where focus returns from the sheet. */
  readonly anchorRef: RefObject<HTMLElement | null>;
  readonly size?: InfoPanelSize;
  /** The panel's id, for the button's `aria-controls`. */
  readonly id?: string | undefined;
}

export function InfoPanel({ size = 'default', ...props }: InfoPanelProps) {
  return size === 'sheet' ? <InfoSheet {...props} /> : <AnchoredInfoPanel size={size} {...props} />;
}

function AnchoredInfoPanel({
  open,
  onClose,
  anchorRef,
  size,
  id,
  ...content
}: Omit<InfoPanelProps, 'size'> & { readonly size: 'default' | 'touch' }) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const position = useAnchoredPlacement(open, anchorRef, panelRef, 'startOrEnd');
  useDismissAnchoredPanel(open, onClose, anchorRef, panelRef);
  // The close has focus when it is pressed and then unmounts, which would drop focus to <body>; it
  // hands focus back to the info button, where Esc and a press outside leave it.
  const closeFromTheButton = () => {
    onClose();
    anchorRef.current?.focus({ preventScroll: true });
  };

  const placement = {
    '--info-panel-top': `${position?.top ?? 0}px`,
    '--info-panel-left': `${position?.left ?? 0}px`,
  } as CSSProperties;

  return createPortal(
    <div aria-live="polite" data-info-panel-live="">
      {open ? (
        <div
          ref={panelRef}
          id={id}
          role="group"
          aria-labelledby={titleId}
          className={styles.panel}
          data-info-panel=""
          data-size={size}
          data-positioned={position !== null}
          style={placement}
        >
          <div className={styles.header}>
            <p id={titleId} className={styles.title} data-part="title">
              {content.title}
            </p>
            <button
              type="button"
              className={styles.close}
              aria-label="Close"
              onClick={closeFromTheButton}
            >
              <Icon name="x" size={16} />
            </button>
          </div>
          <InfoPanelBody {...content} />
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

function InfoSheet({ open, onClose, anchorRef, ...content }: Omit<InfoPanelProps, 'size'>) {
  // `Modal` gives focus back to whatever had it before it opened. A tap does not focus a button
  // in every browser (Safari), so the sheet hands focus to its button itself once it has closed.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) anchorRef.current?.focus({ preventScroll: true });
    wasOpen.current = open;
  }, [open, anchorRef]);

  return (
    <Modal open={open} onClose={onClose} title={content.title} placement="bottom">
      <InfoPanelBody {...content} />
    </Modal>
  );
}

/** The slots under the title, shared by the panel and the sheet. */
function InfoPanelBody({ definition, steps, result, note }: InfoPanelContent) {
  return (
    <div className={styles.body}>
      <p className={styles.definition} data-part="definition">
        {definition}
      </p>
      {steps !== undefined && steps.length > 0 ? (
        <div className={styles.steps} data-part="steps">
          {steps.map((step, index) => (
            <p key={index} className={styles.step} data-part="step">
              {step}
            </p>
          ))}
        </div>
      ) : null}
      {result !== undefined ? (
        <p className={styles.result} data-part="result">
          {result}
        </p>
      ) : null}
      {note !== undefined ? (
        <p className={styles.note} data-part="note">
          {note}
        </p>
      ) : null}
    </div>
  );
}
